import {
  AssistantMessageRole,
  AssistantMessageStatus,
  AssistantStreamEventType,
  AssistantToolCallStatus,
  type AssistantMessage,
  type AssistantMessagePart,
  type AssistantStreamEvent,
  type AssistantToolCallPart,
} from '@xtreed/academy-shared';

export const OPTIMISTIC_PREFIX = 'local-';

export function optimisticUserMessage(
  conversationId: string,
  parts: AssistantMessagePart[],
): AssistantMessage {
  return {
    id: `${OPTIMISTIC_PREFIX}${Date.now()}`,
    conversationId,
    role: AssistantMessageRole.USER,
    parts,
    status: AssistantMessageStatus.COMPLETE,
    createdAt: new Date().toISOString(),
  };
}

function updateStreaming(
  messages: AssistantMessage[],
  update: (parts: AssistantMessagePart[]) => AssistantMessagePart[],
): AssistantMessage[] {
  let index = messages.length - 1;
  while (
    index >= 0 &&
    !(
      messages[index].role === AssistantMessageRole.ASSISTANT &&
      messages[index].status === AssistantMessageStatus.STREAMING
    )
  ) {
    index--;
  }
  if (index === -1) return messages;
  const next = [...messages];
  next[index] = { ...messages[index], parts: update(messages[index].parts) };
  return next;
}

function updateTool(
  parts: AssistantMessagePart[],
  toolCallId: string,
  patch: Partial<AssistantToolCallPart>,
): AssistantMessagePart[] {
  return parts.map((part) =>
    part.type === 'tool_call' && part.id === toolCallId
      ? { ...part, ...patch }
      : part,
  );
}

export function applyStreamEvent(
  messages: AssistantMessage[],
  event: AssistantStreamEvent,
): AssistantMessage[] {
  switch (event.type) {
    case AssistantStreamEventType.RUN_START: {
      const withoutOptimistic = event.userMessage
        ? messages.filter(
            (message) => !message.id.startsWith(OPTIMISTIC_PREFIX),
          )
        : messages;
      return [
        ...withoutOptimistic,
        ...(event.userMessage ? [event.userMessage] : []),
        event.assistantMessage,
      ];
    }
    case AssistantStreamEventType.TEXT_DELTA:
      return updateStreaming(messages, (parts) => {
        const last = parts[parts.length - 1];
        if (last?.type === 'text') {
          return [
            ...parts.slice(0, -1),
            { ...last, text: last.text + event.delta },
          ];
        }
        return [...parts, { type: 'text', text: event.delta }];
      });
    case AssistantStreamEventType.TOOL_START:
      return updateStreaming(messages, (parts) => [
        ...parts,
        {
          type: 'tool_call',
          id: event.toolCallId,
          name: event.name,
          args: event.args,
          status: AssistantToolCallStatus.RUNNING,
          summary: null,
          progress: null,
        },
      ]);
    case AssistantStreamEventType.TOOL_PROGRESS:
      return updateStreaming(messages, (parts) =>
        updateTool(parts, event.toolCallId, { progress: event.progress }),
      );
    case AssistantStreamEventType.TOOL_END:
      return updateStreaming(messages, (parts) =>
        updateTool(parts, event.toolCallId, {
          status: event.status,
          summary: event.summary,
        }),
      );
    case AssistantStreamEventType.APPROVAL_REQUIRED:
      return updateStreaming(messages, (parts) => [...parts, event.part]);
    case AssistantStreamEventType.RUN_END:
      return messages.map((message) =>
        message.id === event.message.id ? event.message : message,
      );
    default:
      return messages;
  }
}

export function markStreamingAs(
  messages: AssistantMessage[],
  status: AssistantMessageStatus,
): AssistantMessage[] {
  return messages.map((message) =>
    message.status === AssistantMessageStatus.STREAMING
      ? { ...message, status }
      : message,
  );
}

export function pendingApproval(messages: AssistantMessage[]) {
  for (let index = messages.length - 1; index >= 0; index--) {
    const part = messages[index].parts.find(
      (item) => item.type === 'approval' && item.status === 'pending',
    );
    if (part && part.type === 'approval') return part;
  }
  return null;
}
